import "./applications.scss";
import {
    type FC,
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import FontAwesomeLight from "../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import { showDialog } from "../../Actions/DialogActions";
import Button from "../../Common/Button";
import HoverError from "../../Utilities/HoverError";
import { useTitle } from "../../Utilities/TitleHook";
import ApplicationCreate from "./ApplicationCreate";
import ApplicationLine, { type ApplicationType } from "./ApplicationLine";
import { ApplicationSecret } from "./ApplicationSecret";

const Applications: FC = () => {
    useTitle("Applications");

    const dispatch = useDispatch();
    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ applications, setApplications ] = useState<Array<ApplicationType>>([]);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState(false);
    const [ createLoading, setCreateLoading ] = useState(false);
    const [ createError, setCreateError ] = useState<false | string>(false);
    const hasAutoOpened = useRef(false);

    const openCreateDialog = useCallback((): void => {
        dispatch(showDialog(
            "Create New Application",
            <ApplicationCreate
                onSubmit={(name: string, redirectUri: string) => {
                    setCreateLoading(true);

                    fetch("/application", {
                        method     : "POST",
                        credentials: "same-origin",
                        headers    : new Headers({
                            "Accept"      : "application/json",
                            "Content-Type": "application/json",
                        }),
                        body: JSON.stringify({
                            name    : name,
                            redirect: redirectUri,
                        }),
                    })
                        .then(response => {
                            setCreateLoading(false);

                            if(response.status !== 200) {
                                console.error("API Documentation: Invalid Response", response);
                                throw new Error("Invalid response");
                            }

                            return response.json();
                        })
                        .then((result) => {
                            setCreateLoading(false);
                            setCreateError(false);

                            // The secret is hashed at rest, so this response is the only chance the
                            // developer gets to read it
                            if(result?.secret) {
                                dispatch(showDialog(
                                    "Application Created",
                                    <ApplicationSecret
                                        clientId={result.client_id}
                                        secret={result.secret}
                                    />
                                ));
                            }
                        })
                        .catch((err) => {
                            console.error(err);
                            setCreateLoading(false);
                            setCreateError(err.toString());
                        });
                }}
            />
        ));
    }, [ dispatch ]);

    useEffect(() => {
        // A fresh signup lands here with ?welcome=1 (set by Register.tsx) so the create-application
        // dialog opens immediately instead of showing an empty list. Only fires once per visit -
        // the param is stripped straight away so refreshing (or coming back later) doesn't reopen it.
        if(hasAutoOpened.current || !searchParams.has("welcome")) {
            return;
        }

        hasAutoOpened.current = true;
        openCreateDialog();

        setSearchParams(params => {
            params.delete("welcome");

            return params;
        }, { replace: true });
    }, [ searchParams, setSearchParams, openCreateDialog ]);

    useEffect(() => {
        if(createLoading) {
            return;
        }

        // Ignore this response if a newer request has since superseded it
        let cancelled = false;

        setLoading(true);

        fetch("/applications", {
            credentials: "same-origin",
            headers    : new Headers({
                "Accept": "application/json",
            }),
        })
            .then((response) => {
                if(cancelled) {
                    return;
                }

                setLoading(false);

                if(response.status !== 200) {
                    throw new Error("Invalid response from server");
                }

                return response.json();
            })
            .then((result) => {
                if(cancelled || typeof result === "undefined") {
                    return;
                }

                setApplications(result);
            })
            .catch((err) => {
                if(cancelled) {
                    return;
                }

                console.error(err);
                setError(err.toString());
            });

        return () => {
            cancelled = true;
        };
    }, [ createLoading ]);

    let before: ReactNode = "";

    if(createError) {
        before = <HoverError>{`An error occurred while creating the application: ${createError}`}</HoverError>;
    }

    if(error) {
        before = <HoverError>{error}</HoverError>;
    }

    let applicationText = <p>We are loading your applications...</p>;

    if(!loading) {
        if(!applications.length) {
            applicationText = (
                <div className="applications-empty">
                    <p>You don&#39;t have any applications yet.</p>
                    <Button onClick={openCreateDialog}>
                        <FontAwesomeLight icon="plus" /> Create your first application
                    </Button>
                </div>
            );
        } else {
            applicationText = (
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Redirect URI</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {applications.map((application, index) => (
                            <ApplicationLine
                                key={application.client_id}
                                index={index}
                                application={application}
                            />
                        ))}
                    </tbody>
                </table>
            );
        }
    }

    return (
        <main id="applications">
            {before}
            <h1 className="text-4xl">
                Your Applications
                <Button onClick={openCreateDialog} classes={[ "create-new" ]}>
                    <FontAwesomeLight icon="plus" /> New
                </Button>
            </h1>
            <div className="mt-4">
                {applicationText}
            </div>
        </main>
    );
};

export default Applications;
